/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { SDK } from '../index';
import { Auth } from '../auth/Auth';

//#region Interfaces
export type IUserDBDataObject = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};
//#endregion

/**
 * This service offers persistence of user specific data and settings.
 */
export class UserData {
  /**
   * @hidden
   */
  static install(instance: SDK) {
    instance['_userData'] = new UserData(instance.Auth);
  }

  private Auth: Auth;

  /**
   * @hidden
   */
  constructor(serviceAuth: Auth) {
    this.Auth = serviceAuth;
  }

  //#region Scopes
  /**
   * Retrieves userdata for the user from all scopes.
   *
   * @param includeSource  Include source.
   * @param idsonly  If true, only the ids without content will be provided.
   */
  public getUserScopes(
    includeSource: boolean,
    idsonly: boolean
  ): Promise<IUserDBDataObject>;
  /**
   * Retrieves userdata for the user from a list of scopes.
   *
   * @param scopes  Valid scopes are workspaces, usersettings, watchlists, widgets, portfolios.
   * @param includeSource  Include source
   * @param idsonly  If true, only the ids without content will be provided.
   */
  public getUserScopes(
    scopes: string[],
    includeSource: boolean,
    idsonly: boolean
  ): Promise<IUserDBDataObject>;
  public getUserScopes(
    scopes?: boolean | string[],
    includeSource = false,
    idsonly = false
  ): Promise<IUserDBDataObject> {
    if (typeof scopes === 'boolean') {
      idsonly = includeSource;
      includeSource = scopes;
      scopes = undefined;
    }
    return this.get('', includeSource, scopes, 'scopes', idsonly);
  }
  //#endregion

  //#region Usersettings
  /**
   * Retrieves all the usersettings for the user.
   *
   * ```js example
   * dory.UserData.getUserSettings()
   *   .catch(function (error) {
   *     console.error(error); // catch api errors
   *   })
   *   .then(function (result) {
   *     console.log(result);
   *   });
   * ```
   *
   * @param includeSource  Include source
   * @param idsonly  If true, only the ids without content will be provided.
   */
  public getUserSettings(
    includeSource: boolean,
    idsonly: boolean
  ): Promise<IUserDBDataObject>;
  /**
   * Retrieves all the usersettings within the given domains for the user.
   *
   * @param domains  Filter list of domains.
   * @param includeSource  Include source
   * @param idsonly  If true, only the ids without content will be provided.
   */
  public getUserSettings(
    domains: string | string[],
    includeSource: boolean,
    idsonly: boolean
  ): Promise<IUserDBDataObject>;
  /**
   * Retrieves all or the given usersettings under a given domain.
   *
   * @param domain  Domain Id within which the usersetting items are requested.
   * @param ids  Filter list of items.
   * @param includeSource  Include source
   * @param idsonly  If true, only the ids without content will be provided.
   */
  public getUserSettings(
    domain: string,
    ids: string[],
    includeSource: boolean,
    idsonly: boolean
  ): Promise<IUserDBDataObject>;
  /**
   * Retrieves a usersetting item in a given domain.
   *
   * ```js example
   * dory.UserData.getUserSettings('applicationSettings', 'general', true)
   *   .catch(function (error) {
   *     console.error(error); // catch api errors
   *   })
   *   .then(function (result) {
   *     console.log(result);
   *   });
   * ```
   *
   * @param domain  Domain Id within which the usersetting items are requested.
   * @param id  Usersetting item which is requested.
   * @param includeSource  Include source
   */
  public getUserSettings(
    domain: string,
    id: string,
    includeSource: boolean
  ): Promise<IUserDBDataObject>;
  public getUserSettings(
    domains?: boolean | string | string[],
    ids?: boolean | string | string[],
    includeSource = false,
    idsonly = false
  ): Promise<IUserDBDataObject> {
    if (typeof domains === 'boolean') {
      includeSource = domains;
      domains = undefined;
      if (typeof ids === 'boolean') {
        idsonly = ids;
        ids = undefined;
      }
    }
    if (typeof domains === 'string' || Array.isArray(domains)) {
      if (typeof ids === 'boolean') {
        idsonly = includeSource;
        includeSource = ids;
        ids = undefined;
      }
    }
    if (typeof domains === 'string') {
      return this.get(
        `/usersettings/${domains}`,
        includeSource,
        ids,
        'itemIds',
        idsonly
      );
    } else {
      return this.get(
        '/usersettings',
        includeSource,
        domains,
        'domains',
        idsonly
      );
    }
  }

  /**
   * Updates or creates several usersettings within given domains.
   *
   * ```js example
   * // update/create usersettings within several domains
   * var data = {
   *   domain1: {
   *     userSetting1: {},
   *     userSetting2: {
   *       theme: 'light',
   *     },
   *   },
   *   domain2: {
   *     userSetting1: {},
   *     userSetting2: {},
   *   },
   * };
   * dory.UserData.updateUserSettings(data)
   *   .catch(function (error) {
   *     console.error(error); // catch api errors
   *   })
   *   .then(function (result) {
   *     if (result.status === 200) {
   *       console.log(result);
   *     }
   *   });
   * ```
   *
   * @param data
   */
  public updateUserSettings(data: any): Promise<IUserDBDataObject>;
  /**
   * Creates or updates the usersettings under a given domain.
   *
   * @param domain  Domain Id under which usersetting items are to be created or updated.
   * @param data
   */
  public updateUserSettings(
    domain: string,
    data: any
  ): Promise<IUserDBDataObject>;
  /**
   * Creates or updates a usersetting in a particular domain.
   *
   * ```js example
   * // create/update usersetting in a particular domain
   * var data = {
   *   theme: 'dark',
   *   icon: 'cog',
   * };
   *
   * dory.UserData.updateUserSettings('domain1', 'userSetting2', data)
   *   .catch(function (error) {
   *     console.error(error); // catch api errors
   *   })
   *   .then(function (result) {
   *     if (result.status === 200) {
   *       console.log(result);
   *     }
   *   });
   * ```
   *
   * @param domain  Domain Id under which usersetting items are to be created or updated.
   * @param id  Usersetting item which is to be created or updated.
   * @param data
   */
  public updateUserSettings(
    domain: string,
    id: string,
    data: any
  ): Promise<IUserDBDataObject>;
  public updateUserSettings(
    data: any,
    domain?: any,
    id?: any
  ): Promise<IUserDBDataObject> {
    if (typeof domain === 'string') {
      return this.update(`/usersettings/${data}`, id, domain);
    }
    return this.update('/usersettings', data, domain);
  }

  /**
   * Deletes all usersettings in all domains
   */
  public deleteUserSettings(): Promise<IUserDBDataObject>;
  /**
   * Deletes all usersettings in the given domains
   *
   * @param domains  list of domains
   */
  public deleteUserSettings(domains: string[]): Promise<IUserDBDataObject>;
  /**
   * Deletes the given usersettings within a given domain
   *
   * ```js example
   * window.dory.UserData.deleteUserSettings('domain1', ['userSetting1', 'userSetting2'])
   *   .catch(function (error) {
   *     console.error(error); // catch api errors
   *   })
   *   .then(function (result) {
   *     if (result.status === 200) {
   *       console.log(result);
   *     }
   *   });
   * ```
   *
   * @param domain  Domain Id under which the usersetting items are to be deleted.
   * @param ids  List of items to be deleted.
   */
  public deleteUserSettings(
    domain: string,
    ids: string | string[]
  ): Promise<IUserDBDataObject>;
  public deleteUserSettings(
    domains?: string | string[],
    ids?: string | string[]
  ): Promise<IUserDBDataObject> {
    if (domains === undefined) {
      return this.Auth.delete('/userdata/usersettings');
    }
    if (typeof domains === 'string') {
      return this.update(`/usersettings/${domains}`, ids);
    }
    return this.delete('/usersettings', domains, 'domains');
  }
  //#endregion

  //#region Workspaces
  /**
   * Retrieves all the workspaces for the user.
   *
   * @param includeSource  Include source
   * @param idsonly  If true, only the ids without content will be provided.
   */
  public getWorkspaces(
    includeSource: boolean,
    idsonly: boolean
  ): Promise<IUserDBDataObject>;
  /**
   * Retrieves a workspace.
   *
   * @param id  Item to be retrieved
   */
  public getWorkspaces(id: string): Promise<IUserDBDataObject>;
  /**
   * Retrieves a workspace.
   *
   * @param id  Item to be retrieved
   * @param includeSource  Include source
   */
  public getWorkspaces(
    id: string,
    includeSource: boolean
  ): Promise<IUserDBDataObject>;
  /**
   * Retrieves all workspaces for the user.
   *
   * @param ids  Filter list of items.
   */
  public getWorkspaces(ids: string[]): Promise<IUserDBDataObject>;
  /**
   * Retrieves all workspaces for the user.
   *
   * @param ids  Filter list of items.
   * @param includeSource  Include source
   * @param idsonly  If true, only the ids without content will be provided.
   */
  public getWorkspaces(
    ids: string[],
    includeSource: boolean,
    idsonly: boolean
  ): Promise<IUserDBDataObject>;
  public getWorkspaces(
    ids?: boolean | string | string[],
    includeSource = false,
    idsonly = false
  ): Promise<IUserDBDataObject> {
    return this.get('/workspaces', includeSource, ids, 'itemIds', idsonly);
  }

  /**
   * Updates or creates several workspaces.
   *
   * @param data
   */
  public updateWorkspaces(data: any): Promise<IUserDBDataObject>;
  /**
   * Creates or updates a workspace.
   *
   * @param id Item to be retrieved
   * @param data
   */
  public updateWorkspaces(id: string, data: any): Promise<IUserDBDataObject>;
  public updateWorkspaces(data: any, id?: any): Promise<IUserDBDataObject> {
    return this.update('/workspaces', data, id);
  }

  /**
   * Deletes a single/list of workspaces.
   *
   * @param ids  Filter list of items.
   */
  public deleteWorkspaces(ids: string | string[]): Promise<IUserDBDataObject> {
    return this.delete('/workspaces', ids);
  }
  //#endregion

  //#region Watchlists
  /**
   * Retrieves all the watchlists for the user.
   *
   * @param includeSource  Include source
   * @param idsonly  If true, only the ids without content will be provided.
   */
  public getWatchlists(
    includeSource: boolean,
    idsonly: boolean
  ): Promise<IUserDBDataObject>;
  /**
   * Retrieves a watchlist.
   *
   * @param id  Item to be retrieved
   */
  public getWatchlists(id: string): Promise<IUserDBDataObject>;
  /**
   * Retrieves a watchlist.
   *
   * @param id  Item to be retrieved
   * @param includeSource  Include source
   */
  public getWatchlists(
    id: string,
    includeSource: boolean
  ): Promise<IUserDBDataObject>;
  /**
   * Retrieves all watchlists for the user.
   *
   * @param ids  Filter list of items.
   */
  public getWatchlists(ids: string[]): Promise<IUserDBDataObject>;
  /**
   * Retrieves all watchlists for the user.
   *
   * @param ids  Filter list of items.
   * @param includeSource  Include source
   * @param idsonly  If true, only the ids without content will be provided.
   */
  public getWatchlists(
    ids: string[],
    includeSource: boolean,
    idsonly: boolean
  ): Promise<IUserDBDataObject>;
  public getWatchlists(
    ids?: boolean | string | string[],
    includeSource = false,
    idsonly = false
  ): Promise<IUserDBDataObject> {
    return this.get('/watchlists', includeSource, ids, 'itemIds', idsonly);
  }

  /**
   * Updates or creates several watchlists.
   *
   * @param data
   */
  public updateWatchlists(data: any): Promise<IUserDBDataObject>;
  /**
   * Creates or updates a watchlist.
   *
   * @param id  Item to be retrieved
   * @param data
   */
  public updateWatchlists(id: string, data: any): Promise<IUserDBDataObject>;
  public updateWatchlists(data: any, id?: any): Promise<IUserDBDataObject> {
    return this.update('/watchlists', data, id);
  }

  /**
   * Deletes a single/list of watchlists.
   *
   * @param ids  Filter list of items.
   */
  public deleteWatchlists(ids: string | string[]): Promise<IUserDBDataObject> {
    return this.delete('/watchlists', ids);
  }
  //#endregion

  //#region Widgets
  /**
   * Retrieves all the widgets for the user.
   *
   * @param includeSource  Include source
   * @param idsonly  If true, only the ids without content will be provided.
   */
  public getWidgets(
    includeSource: boolean,
    idsonly: boolean
  ): Promise<IUserDBDataObject>;
  /**
   * Retrieves a widget.
   *
   * @param id  Item to be retrieved
   */
  public getWidgets(id: string): Promise<IUserDBDataObject>;
  /**
   * Retrieves a widget.
   *
   * @param id  Item to be retrieved
   * @param includeSource  Include source
   */
  public getWidgets(
    id: string,
    includeSource: boolean
  ): Promise<IUserDBDataObject>;
  /**
   * Retrieves all widgets for the user.
   *
   * @param ids  Filter list of items.
   */
  public getWidgets(ids: string[]): Promise<IUserDBDataObject>;
  /**
   * Retrieves all widgets for the user.
   *
   * @param ids  Filter list of items.
   * @param includeSource  Include source
   * @param idsonly  If true, only the ids without content will be provided.
   */
  public getWidgets(
    ids: string[],
    includeSource: boolean,
    idsonly: boolean
  ): Promise<IUserDBDataObject>;
  public getWidgets(
    ids?: boolean | string | string[],
    includeSource = false,
    idsonly = false
  ): Promise<IUserDBDataObject> {
    return this.get('/widgets', includeSource, ids, 'itemIds', idsonly);
  }

  /**
   * Updates or creates several widgets.
   *
   * @param data
   */
  public updateWidgets(data: any): Promise<IUserDBDataObject>;
  /**
   * Creates or updates a widget.
   *
   * @param id  Item to be retrieved
   * @param data
   */
  public updateWidgets(id: string, data: any): Promise<IUserDBDataObject>;
  public updateWidgets(data: any, id?: any): Promise<IUserDBDataObject> {
    return this.update('/widgets', data, id);
  }

  /**
   * Deletes a single/list of widgets.
   *
   * @param ids  Filter list of items.
   */
  public deleteWidgets(ids: string | string[]): Promise<IUserDBDataObject> {
    return this.delete('/widgets', ids);
  }
  //#endregion

  //#region Portfolios
  /**
   * Retrieves all the portfolios for the user.
   *
   * @param includeSource  Include source
   * @param idsonly  If true, only the ids without content will be provided.
   */
  public getPortfolios(
    includeSource: boolean,
    idsonly: boolean
  ): Promise<IUserDBDataObject>;
  /**
   * Retrieves a portfolio.
   *
   * @param id  Item to be retrieved
   */
  public getPortfolios(id: string): Promise<IUserDBDataObject>;
  /**
   * Retrieves a portfolio.
   *
   * @param id  Item to be retrieved
   * @param includeSource  Include source
   */
  public getPortfolios(
    id: string,
    includeSource: boolean
  ): Promise<IUserDBDataObject>;
  /**
   * Retrieves all portfolios for the user.
   *
   * @param ids Filter list of items.
   */
  public getPortfolios(ids: string[]): Promise<IUserDBDataObject>;
  /**
   * Retrieves all portfolios for the user.
   *
   * @param ids Filter list of items.
   * @param includeSource  Include source
   * @param idsonly  If true, only the ids without content will be provided.
   */
  public getPortfolios(
    ids: string[],
    includeSource: boolean,
    idsonly: boolean
  ): Promise<IUserDBDataObject>;
  public getPortfolios(
    ids?: boolean | string | string[],
    includeSource = false,
    idsonly = false
  ): Promise<IUserDBDataObject> {
    return this.get('/portfolios', includeSource, ids, 'itemIds', idsonly);
  }

  /**
   * Updates or creates several portfolios.
   *
   * @param data
   */
  public updatePortfolios(data: any): Promise<IUserDBDataObject>;
  /**
   * Creates or updates a portfolios.
   *
   * @param id  Item to be retrieved
   * @param data
   */
  public updatePortfolios(id: string, data: any): Promise<IUserDBDataObject>;
  public updatePortfolios(data: any, id?: any): Promise<IUserDBDataObject> {
    return this.update('/portfolios', data, id);
  }

  /**
   * Deletes a single/list of portfolios.
   *
   * @param ids  Filter list of items.
   */
  public deletePortfolios(ids: string | string[]): Promise<IUserDBDataObject> {
    return this.delete('/portfolios', ids);
  }
  //#endregion

  private get(
    scope: string,
    includeSource: boolean,
    ids: boolean | string | string[],
    paramName = 'itemIds',
    idsonly?: boolean
  ): Promise<IUserDBDataObject> {
    if (typeof ids === 'boolean') {
      includeSource = ids;
    }
    let url = `/userdata${scope}`;
    if (typeof ids === 'string') {
      url += `/${ids}?`;
    } else if (typeof ids === 'object') {
      url += this.generateURLParams(ids, paramName);
    } else {
      // boolean/undefined
      url += '?';
    }
    url += `&includeSource=${includeSource}`;
    url += idsonly !== undefined ? `&idsonly=${idsonly}` : '';
    return this.Auth.get(url);
  }

  private update(
    scope: string,
    data: any,
    id?: any
  ): Promise<IUserDBDataObject> {
    if (typeof data === 'string') {
      const _data = id;
      id = data;
      data = _data;
    }
    return this.Auth.put(`/userdata${scope}${id ? '/' + id : ''}`, data);
  }

  private delete(
    scope: string,
    ids: string | string[],
    paramName = 'itemIds'
  ): Promise<IUserDBDataObject> {
    let url = `/userdata${scope}`;
    if (typeof ids === 'string') {
      url += `/${ids}`;
    } else if (typeof ids === 'object') {
      url += this.generateURLParams(ids, paramName);
    }
    return this.Auth.delete(url);
  }

  private generateURLParams(values: string[], key: string): string {
    return (
      '?' +
      values.reduce(
        (all: string, value: string) => (all += `&${key}=${value}`),
        ''
      )
    );
  }
}
